import {IconButton} from '@wandb/components';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'Pill' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Pill} from '@wandb/weave/components/Tag/Pill';
import {Tailwind} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import React, {useContext} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link, useHistory} from 'react-router-dom';

import {useRampFlagStarredRegistries} from '../../../util/rampFeatureFlags';
import * as urls from '../../../util/urls';
import {capitalizeFirstLetter} from '../../PanelScalarChart/common';
import {formatNumber} from '../../util/formatNumber';
import {
  getDisplayNameForRegistryAccess,
  getIconForRegistryAccess,
  getTooltipTextForRegistryAccess,
} from '../common/permissionUtils';
import {
  DisplayMatchedText,
  RegistriesSearchContext,
} from '../common/RegistriesSearchContextProvider';
import {StarredRegistriesContext} from '../common/StarredRegistriesContextProvider';
import {
  DEFAULT_REGISTRY_PROJECT_TYPES,
  globalRegistryType,
} from '../common/utils';

export const CARD_HEIGHT = '142px';
export const CARD_MIN_WIDTH = '272px';

type RegistryCardProps = globalRegistryType & {
  canViewCollectionCount: boolean;
  isRegistryAvailableToJoin: boolean;
};

export const RegistryCard = (props: RegistryCardProps) => {
  const history = useHistory();
  const {
    orgName,
    orgId,
    registryName,
    description,
    types,
    collectionCount,
    access,
    allowAllArtifactTypesInRegistry,
    canViewCollectionCount,
    isRegistryAvailableToJoin,
  } = props;
  const {searchFilterRegex} = useContext(RegistriesSearchContext);
  const isDefaultRegistry =
    DEFAULT_REGISTRY_PROJECT_TYPES.includes(registryName);

  const showRegistryStar = useRampFlagStarredRegistries(orgId);

  const {onStarClick, starredRegistries} = useContext(StarredRegistriesContext);
  const isStarred = starredRegistries[props.id] != null;

  return (
    <Tailwind style={{height: CARD_HEIGHT, minWidth: CARD_MIN_WIDTH}}>
      <Link to={urls.globalRegistry(orgName, registryName)}>
        <div
          data-test={`registry-${registryName}`}
          className="group flex h-full w-full cursor-pointer flex-col items-start justify-between gap-8 rounded border border-moon-200 bg-white p-16 hover:border-teal-350">
          <div className="flex w-full flex-col items-start">
            <div className="flex w-full items-center gap-8">
              <div
                className="flex w-full truncate text-xl leading-6 text-moon-800"
                title={capitalizeFirstLetter(registryName)}>
                {searchFilterRegex ? (
                  <DisplayMatchedText
                    text={capitalizeFirstLetter(registryName)}
                    searchFilterRegex={searchFilterRegex}
                  />
                ) : (
                  capitalizeFirstLetter(registryName)
                )}
              </div>
              {showRegistryStar && (
                <div className="shrink-0">
                  <div
                    className={[
                      !isStarred && 'invisible group-hover:visible',
                      // TODO: can remove this once IconButton allows color customization
                      isStarred
                        ? '[&>button]:![--button-text-color:theme(colors.gold.400)]'
                        : '[&>button]:![--button-text-color:theme(colors.moon.500)]',
                    ].join(' ')}>
                    <IconButton
                      aria-label={
                        isStarred ? 'Unstar registry' : 'Star registry'
                      }
                      data-test={
                        isStarred
                          ? 'registry-star-filled'
                          : 'registry-star-empty'
                      }
                      icon={isStarred ? 'star-filled' : 'star'}
                      variant="ghost"
                      size="sm"
                      onClick={async e => {
                        e.preventDefault();
                        try {
                          await onStarClick(
                            props,
                            !isStarred,
                            'registry custom card'
                          );
                        } catch (err) {
                          toast('Failed to update star for this registry.');
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              className="w-full truncate text-sm font-normal leading-5 text-moon-500"
              title={description}>
              {description}
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-8">
            <div
              className="flex w-full items-start gap-10 overflow-auto"
              style={{scrollbarWidth: 'none'}}>
              {!allowAllArtifactTypesInRegistry ? (
                types?.map(type => {
                  return (
                    <Tooltip
                      key={type}
                      trigger={
                        <div className="cursor-pointer">
                          <Pill color="moon" label={type} />
                        </div>
                      }
                      content={`Artifact type: ${type}`}
                      position="bottom left"
                    />
                  );
                })
              ) : (
                <Pill color="moon" label={'All types'} />
              )}
            </div>
            <div className="flex w-full items-center">
              <div className="flex items-center gap-8">
                <div className="flex items-center justify-center gap-2">
                  {canViewCollectionCount && (
                    <>
                      <Icon
                        name="row-height-small"
                        className="h-12 w-12 text-moon-450"
                      />
                      <div className="text-xs text-moon-450">
                        {`${formatNumber(collectionCount)} ${maybePluralizeWord(
                          collectionCount,
                          'collection',
                          's'
                        )}`}
                      </div>
                    </>
                  )}
                </div>
                <Tooltip
                  position="bottom left"
                  content={getTooltipTextForRegistryAccess(access)}
                  trigger={
                    <div className="group flex cursor-pointer items-center justify-center gap-2">
                      <Icon
                        name={getIconForRegistryAccess(access)}
                        className="h-12 w-12 text-moon-450"
                      />
                      <div className="text-xs text-moon-450">
                        {getDisplayNameForRegistryAccess(access)}
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="flex ml-auto items-center gap-8">
                {isDefaultRegistry && <Pill color="teal" label="Core" />}
                {isRegistryAvailableToJoin && (
                  <Pill
                    color="gold"
                    label="Available to join"
                    icon="user-author"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </Tailwind>
  );
};
